import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  OnChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { Subject } from 'rxjs/Subject';
import {hasServerErrorCode} from 'app/services/server-error-code';
import 'rxjs/add/operator/takeUntil';

import { LearningFacadeService } from '../../../services/learning-facade.service';
import {
  SkillTestGenerateResponse,
  SkillLearningHttpError,
  SkillTestQuestion,
  SkillTestQuestionViewModel,
  SkillTestResultResponse,
  SkillTestViewState,
} from '../../../models/skill-learning.model';
import {
  SKILL_CONFIGURATION_ERROR,
  SKILL_HTTP_STATUS,
  SKILL_LEARNING_EMPTY_COUNT,
  SKILL_LEARNING_EMPTY_TEXT,
  SKILL_LEARNING_FIRST_INDEX,
  SKILL_LEARNING_LEVEL_COUNT,
  SKILL_LEARNING_NOT_FOUND_INDEX,
  SKILL_TEST_ERROR_TOKEN,
  SKILL_TEST_MESSAGE,
  SKILL_TEST_TIME,
  SKILL_TEST_VIEW_STATE,
} from '../../../options/skill-learning.option';
const GROWTH_RULES_BUSY = 'GROWTH_RULES_BUSY';

@Component({
  selector: 'app-test-container',
  templateUrl: './test-container.component.html',
  styleUrls: ['./test-container.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestContainerComponent implements OnInit, OnChanges, OnDestroy {

  @Input() specializationId: string;
  @Input() levelNumber = SKILL_LEARNING_LEVEL_COUNT.ONE;
  @Input() isLevelPassed = false;
  @Input() testRetakeCooldownHours = 10;
  @Input() testGenerationLimitPerUserPerDay = 2;
  @Input() showCloseButton = true;
  @Input() showPassedAction = true;
  @Input() isBlockedByCuratorReview = false;
  @Input() isDisabled = false;

  @Output() testCompleted = new EventEmitter<SkillTestResultResponse>();
  @Output() nextLevelRequested = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  state: SkillTestViewState = SKILL_TEST_VIEW_STATE.IDLE;
  test: SkillTestGenerateResponse;
  displayedQuestions: SkillTestQuestionViewModel[] = [];
  selectedAnswers: { [questionIndex: string]: number } = {};
  result: SkillTestResultResponse;
  submissionPending = false;
  submissionRetryAvailable = false;
  private submissionId: string;
  cooldownSeconds = 0;
  errorMessage = '';
  errorMessageParams: { [key: string]: number } = {};
  errorHelpMessage = '';
  errorHelpMessageParams: { [key: string]: number } = {};
  timeRemaining = 0;
  isSubmitting = false;

  private readonly destroy$ = new Subject<void>();
  private readonly contextChanged$ = new Subject<void>();
  private timerInterval: NodeJS.Timer | null = null;
  private testDeadlineAt = 0;
  private contextSpecializationId: string | null = null;
  private contextLevelNumber: number | null = null;

  constructor(
    private facade: LearningFacadeService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.storeContextIdentity();
  }

  ngOnChanges(): void {
    if (!this.hasContextIdentityChanged()) {
      return;
    }
    this.contextChanged$.next();
    this.storeContextIdentity();
    this.resetContextState();
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.contextChanged$.next();
    this.contextChanged$.complete();
    this.clearTimer();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get retryHintParams(): { [key: string]: number } {
    return { hours: this.testRetakeCooldownHours };
  }

  get retryRuleParams(): { [key: string]: number } {
    return this.buildRetryRuleParams();
  }

  get shouldShowRetryRuleHintBeforeStart(): boolean {
    return true;
  }

  get canShowStartCard(): boolean {
    return !this.isBlockedByCuratorReview;
  }

  get canShowCuratorReviewBlockedMessage(): boolean {
    return this.state === SKILL_TEST_VIEW_STATE.IDLE && this.isBlockedByCuratorReview;
  }

  get canShowPassedAction(): boolean {
    return this.showPassedAction && !!this.result && this.result.passed;
  }

  get canShowResultRetryAction(): boolean {
    return !!this.result && !this.isBlockedByCuratorReview;
  }

  get formattedTimeRemaining(): string {
    const minutes = Math.floor(this.timeRemaining / 60);
    const seconds = this.timeRemaining % 60;
    return `${this.formatTwoDigits(minutes)}:${this.formatTwoDigits(seconds)}`;
  }

  get shouldShowHeaderTimer(): boolean {
    return this.state === SKILL_TEST_VIEW_STATE.TESTING;
  }

  get canShowWorkspaceBackground(): boolean {
    return this.canShowSystemCard;
  }

  get canShowSystemCard(): boolean {
    return this.state !== SKILL_TEST_VIEW_STATE.TESTING;
  }

  get canShowErrorCloseAction(): boolean {
    return false;
  }

  onStartTest(): void {
    if (this.isDisabled) {
      return;
    }
    if (this.isBlockedByCuratorReview) {
      this.showCuratorReviewAlreadyPassedError();
      return;
    }
    this.state = SKILL_TEST_VIEW_STATE.GENERATING;
    this.clearErrorMessage();
    this.cdr.markForCheck();

    this.facade.generateTest(this.specializationId, this.levelNumber)
      .takeUntil(this.contextChanged$)
      .takeUntil(this.destroy$)
      .subscribe(
        test => this.handleTestGenerated(test),
        error => this.handleTestError(error),
      );
  }

  onAnswerSelected(originalIndex: number, answerIndex: number): void {
    this.selectedAnswers[String(originalIndex)] = answerIndex;
    this.cdr.markForCheck();
  }

  onSubmitTest(): void {
    if (!this.canSubmitAnswers) {
      return;
    }
    this.submitCurrentTest();
  }

  onClose(): void {
    this.clearTimer();
    this.closed.emit();
    this.cdr.markForCheck();
  }

  onContinueToNextLevel(): void {
    this.nextLevelRequested.emit();
  }

  onRetryTest(): void {
    if (this.isDisabled) {
      return;
    }
    this.resetTestResult();
    this.onStartTest();
  }

  get hasSelectedAnswers(): boolean {
    return Object.keys(this.selectedAnswers).length > 0;
  }

  get canSubmitAnswers(): boolean {
    return (this.hasSelectedAnswers || this.submissionRetryAvailable) && !this.isSubmitting;
  }

  trackByQuestionIndex(index: number, question: SkillTestQuestionViewModel): number {
    return question ? question.originalIndex : index;
  }

  trackByAnswerIndex(index: number): number {
    return index;
  }

  isAnswerSelected(originalIndex: number, answerIndex: number): boolean {
    return this.selectedAnswers[String(originalIndex)] === answerIndex;
  }

  private startTimer(minutes: number): void {
    this.testDeadlineAt = Date.now() + minutes * 60 * 1000;
    this.timeRemaining = this.calculateTimeRemaining();
    this.cdr.markForCheck();
    this.timerInterval = setInterval(() => {
      this.timeRemaining = this.calculateTimeRemaining();
      this.cdr.markForCheck();
      this.tryAutoSubmitExpiredTest();
    }, 1000);
  }

  private clearTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
      this.cdr.markForCheck();
    }
    this.testDeadlineAt = 0;
  }

  private autoSubmitTest(): void {
    if (!this.canAutoSubmitTest()) {
      return;
    }
    this.submitCurrentTest();
  }

  private handleTestError(error: SkillLearningHttpError): void {
    this.state = SKILL_TEST_VIEW_STATE.ERROR;
    this.clearErrorMessage();
    this.applyTestError(error);
    this.cdr.markForCheck();
  }

  private applyTestError(error: SkillLearningHttpError): void {
    if (error.status === SKILL_HTTP_STATUS.CONFLICT) {
      this.handleConflictError(error);
    } else if (error.status === SKILL_HTTP_STATUS.TOO_MANY_REQUESTS) {
      this.loadCooldownWithFallback();
    } else if (error.status === SKILL_HTTP_STATUS.PRECONDITION_FAILED
      && this.isLevelNotAvailable(error)) {
      this.setTestErrorMessage(SKILL_TEST_MESSAGE.LEVEL_NOT_AVAILABLE);
    } else if (error.status === SKILL_HTTP_STATUS.PRECONDITION_FAILED
      && this.isTestBankNotReady(error)) {
      this.setTestErrorMessage(SKILL_TEST_MESSAGE.BANK_NOT_READY);
    } else if (error.status === SKILL_HTTP_STATUS.SERVICE_UNAVAILABLE) {
      this.setTestErrorMessage(SKILL_TEST_MESSAGE.SERVICE_UNAVAILABLE);
    } else {
      this.setTestErrorMessage(SKILL_TEST_MESSAGE.GENERIC_ERROR);
    }
  }

  private handleTestGenerated(test: SkillTestGenerateResponse): void {
    this.test = test;
    this.displayedQuestions = this.createDisplayedQuestions(test);
    this.selectedAnswers = {};
    this.state = SKILL_TEST_VIEW_STATE.TESTING;
    this.startTimer(test.timeLimitMinutes);
    this.cdr.markForCheck();
  }

  private setTestErrorMessage(message: string): void {
    this.errorMessage = message;
    this.errorMessageParams = {};
  }

  private loadCooldownWithFallback(): void {
    this.facade.getTestCooldown(this.specializationId, this.levelNumber)
      .takeUntil(this.contextChanged$)
      .takeUntil(this.destroy$)
      .subscribe(
        seconds => this.handleCooldownLoaded(seconds),
        () => this.showTestGenerationLimitError(),
      );
  }

  private handleCooldownLoaded(seconds: number): void {
    if (seconds > 0) {
      this.cooldownSeconds = seconds;
      this.formatCooldown();
      this.cdr.markForCheck();
      return;
    }
    this.showTestGenerationLimitError();
  }

  private formatCooldown(): void {
    const totalMinutes = Math.max(
      SKILL_TEST_TIME.MINIMUM_COOLDOWN_MINUTES,
      Math.ceil(this.cooldownSeconds / SKILL_TEST_TIME.SECONDS_PER_MINUTE),
    );
    const hours = Math.floor(totalMinutes / SKILL_TEST_TIME.MINUTES_PER_HOUR);
    const minutes = totalMinutes % SKILL_TEST_TIME.MINUTES_PER_HOUR;
    if (hours > SKILL_LEARNING_EMPTY_COUNT) {
      this.errorMessage = SKILL_TEST_MESSAGE.RETRY_IN_HOURS;
      this.errorMessageParams = { hours: hours, minutes: minutes };
      this.setRetryRuleHelpMessage();
      return;
    }
    this.errorMessage = SKILL_TEST_MESSAGE.RETRY_IN_MINUTES;
    this.errorMessageParams = { minutes: minutes };
    this.setRetryRuleHelpMessage();
  }

  private resetTestResult(): void {
    this.resetContextState();
  }

  private resetContextState(): void {
    this.submissionId = null;
    this.submissionPending = false;
    this.submissionRetryAvailable = false;
    this.clearTimer();
    this.result = null;
    this.selectedAnswers = {};
    this.displayedQuestions = [];
    this.test = null;
    this.state = SKILL_TEST_VIEW_STATE.IDLE;
    this.isSubmitting = false;
    this.cooldownSeconds = SKILL_LEARNING_EMPTY_COUNT;
    this.timeRemaining = SKILL_LEARNING_EMPTY_COUNT;
    this.clearErrorMessage();
  }

  private clearErrorMessage(): void {
    this.errorMessage = SKILL_LEARNING_EMPTY_TEXT;
    this.errorMessageParams = {};
    this.errorHelpMessage = SKILL_LEARNING_EMPTY_TEXT;
    this.errorHelpMessageParams = {};
  }

  private showTestGenerationLimitError(): void {
    this.errorMessage = SKILL_TEST_MESSAGE.DAILY_LIMIT_REACHED;
    this.errorMessageParams = {
      limit: this.testGenerationLimitPerUserPerDay,
    };
    this.setRetryRuleHelpMessage();
    this.cdr.markForCheck();
  }

  private handleConflictError(error: SkillLearningHttpError): void {
    if (this.isCuratorReviewAlreadyPassed(error)) {
      this.showCuratorReviewAlreadyPassedError();
      return;
    }
    this.errorMessage = SKILL_TEST_MESSAGE.GENERIC_ERROR;
    this.errorMessageParams = {};
  }

  private showCuratorReviewAlreadyPassedError(): void {
    this.state = SKILL_TEST_VIEW_STATE.ERROR;
    this.errorMessage = SKILL_TEST_MESSAGE.REVIEW_ALREADY_PASSED;
    this.errorMessageParams = {};
    this.errorHelpMessage = SKILL_LEARNING_EMPTY_TEXT;
    this.errorHelpMessageParams = {};
    this.cdr.markForCheck();
  }

  private setRetryRuleHelpMessage(): void {
    this.errorHelpMessage = SKILL_TEST_MESSAGE.RETRY_RULE_HINT;
    this.errorHelpMessageParams = this.buildRetryRuleParams();
  }

  private buildRetryRuleParams(): { [key: string]: number } {
    return {
      hours: this.testRetakeCooldownHours,
      limit: this.testGenerationLimitPerUserPerDay,
    };
  }

  private submitCurrentTest(): void {
    this.isSubmitting = true;
    this.clearTimer();
    this.state = SKILL_TEST_VIEW_STATE.GENERATING;
    this.cdr.markForCheck();
    const request = this.submissionId ? {submissionId: this.submissionId, answers: this.selectedAnswers}
      : {answers: this.selectedAnswers};
    this.facade.submitTest(this.specializationId, request, this.levelNumber)
      .takeUntil(this.contextChanged$)
      .takeUntil(this.destroy$)
      .subscribe(
        result => this.handleSubmitSuccess(result),
        error => this.handleSubmitError(error),
      );
  }

  private handleSubmitSuccess(result: SkillTestResultResponse): void {
    this.isSubmitting = false;
    this.submissionPending = result.pending === true;
    this.submissionId = this.submissionPending ? result.submissionId : null;
    this.submissionRetryAvailable = this.submissionPending;
    if (this.submissionPending) {
      this.result = null;
      this.state = SKILL_TEST_VIEW_STATE.ERROR;
      this.errorMessage = SKILL_TEST_MESSAGE.SUBMISSION_PENDING;
      this.cdr.markForCheck();
      return;
    }
    this.result = result;
    this.state = SKILL_TEST_VIEW_STATE.RESULT;
    this.testCompleted.emit(result);
    this.cdr.markForCheck();
  }

  private handleSubmitError(error: SkillLearningHttpError): void {
    this.isSubmitting = false;
    this.submissionRetryAvailable = this.submissionPending || this.isSubmissionBusy(error);
    this.handleTestError(error);
  }

  private isSubmissionBusy(error: SkillLearningHttpError): boolean {
    if (!error) { return false; }
    try {
      return hasServerErrorCode(error.json ? {error: error.json()} : error, GROWTH_RULES_BUSY);
    } catch (invalidResponse) { return false; }
  }

  private calculateTimeRemaining(): number {
    if (!this.testDeadlineAt) {
      return SKILL_LEARNING_EMPTY_COUNT;
    }
    return Math.max(
      SKILL_LEARNING_EMPTY_COUNT,
      Math.ceil((this.testDeadlineAt - Date.now()) / SKILL_TEST_TIME.MILLISECONDS_PER_SECOND),
    );
  }

  private tryAutoSubmitExpiredTest(): void {
    if (this.state !== SKILL_TEST_VIEW_STATE.TESTING
      || this.timeRemaining > SKILL_LEARNING_EMPTY_COUNT) {
      return;
    }
    this.autoSubmitTest();
  }

  private canAutoSubmitTest(): boolean {
    return !!this.test && !this.isSubmitting;
  }

  private isCuratorReviewAlreadyPassed(error: SkillLearningHttpError): boolean {
    const text = this.extractErrorText(error).toLowerCase();
    return text.indexOf(SKILL_TEST_ERROR_TOKEN.CURATOR_REVIEW) !== SKILL_LEARNING_NOT_FOUND_INDEX
      || text.indexOf(SKILL_TEST_ERROR_TOKEN.REVIEW_ALREADY_PASSED) !== SKILL_LEARNING_NOT_FOUND_INDEX
      || text.indexOf(SKILL_TEST_ERROR_TOKEN.ALREADY_COMPLETED_WITH_CURATOR)
        !== SKILL_LEARNING_NOT_FOUND_INDEX;
  }

  private isTestBankNotReady(error: SkillLearningHttpError): boolean {
    const text = this.extractErrorText(error).toLowerCase();
    return text.indexOf(SKILL_TEST_ERROR_TOKEN.BANK_NOT_READY) !== SKILL_LEARNING_NOT_FOUND_INDEX;
  }

  private isLevelNotAvailable(error: SkillLearningHttpError): boolean {
    return this.extractErrorText(error).indexOf(SKILL_CONFIGURATION_ERROR.LEVEL_NOT_AVAILABLE)
      >= SKILL_LEARNING_FIRST_INDEX;
  }
  private extractErrorText(error: SkillLearningHttpError): string {
    if (!error) {
      return SKILL_LEARNING_EMPTY_TEXT;
    }
    return this.extractJsonErrorText(error) || this.extractPlainErrorText(error);
  }

  private extractJsonErrorText(error: SkillLearningHttpError): string {
    if (!error.json) {
      return SKILL_LEARNING_EMPTY_TEXT;
    }
    try {
      const body = error.json();
      return body && (body.message || body.code)
        ? String(body.message || body.code)
        : SKILL_LEARNING_EMPTY_TEXT;
    } catch (ignored) {
      return SKILL_LEARNING_EMPTY_TEXT;
    }
  }

  private extractPlainErrorText(error: SkillLearningHttpError): string {
    if (error.text) {
      try {
        return error.text() || SKILL_LEARNING_EMPTY_TEXT;
      } catch (ignored) {
        return SKILL_LEARNING_EMPTY_TEXT;
      }
    }
    return error._body || SKILL_LEARNING_EMPTY_TEXT;
  }

  private formatTwoDigits(value: number): string {
    return value < 10 ? `0${value}` : String(value);
  }

  private createDisplayedQuestions(test: SkillTestGenerateResponse): SkillTestQuestionViewModel[] {
    if (!test || !test.questions) {
      return [];
    }
    return this.shuffleQuestions(this.mapQuestions(test.questions));
  }

  private mapQuestions(questions: SkillTestQuestion[]): SkillTestQuestionViewModel[] {
    return questions.map((question, index) => ({
      originalIndex: index,
      questionText: question.questionText,
      questionTextTranslation: question.questionTextTranslation,
      answers: question.answers,
      answerTranslations: question.answerTranslations,
    }));
  }

  private shuffleQuestions(questions: SkillTestQuestionViewModel[]): SkillTestQuestionViewModel[] {
    const shuffled = questions.slice();
    let currentIndex = shuffled.length - SKILL_LEARNING_LEVEL_COUNT.ONE;
    while (currentIndex > SKILL_LEARNING_FIRST_INDEX) {
      this.swapQuestions(
        shuffled,
        currentIndex,
        this.randomIndex(currentIndex + SKILL_LEARNING_LEVEL_COUNT.ONE),
      );
      currentIndex -= SKILL_LEARNING_LEVEL_COUNT.ONE;
    }
    return shuffled;
  }

  private swapQuestions(questions: SkillTestQuestionViewModel[], leftIndex: number, rightIndex: number): void {
    const current = questions[leftIndex];
    questions[leftIndex] = questions[rightIndex];
    questions[rightIndex] = current;
  }

  private randomIndex(limit: number): number {
    return Math.floor(Math.random() * limit);
  }

  private hasContextIdentityChanged(): boolean {
    return this.contextSpecializationId !== this.specializationId
      || this.contextLevelNumber !== this.levelNumber;
  }

  private storeContextIdentity(): void {
    this.contextSpecializationId = this.specializationId;
    this.contextLevelNumber = this.levelNumber;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/components/user/test/test-container.component.ts